import GATSBY_COMPILED_MDX from "/opt/build/repo/src/posts/research/70b-infrastructure.mdx";
import Layout from "./Layout";
import React from "react";
import striptags from "striptags";
import styled from "styled-components";
import {Button} from "./Button";
import {CodeHighlight} from "./CodeHighlight";
import {Footer} from "./Footer";
import {Helmet} from "react-helmet";
import {Image} from "./Image";
import {Link, graphql} from "gatsby";
import {MDXProvider} from "@mdx-js/react";
import {PostTitle} from "./PostTitle";
import {TesterButton} from "./TesterButton";
import {containerStyles} from "./PageComponents";
import {create} from "random-seed";
import {renderTitleWithEmphasis} from "../helpers";
import {useColors} from "../hooks/colors";
export const Wrapper = (styled.div)`
  ${containerStyles}
  padding: 0px;
  max-width: 934px;
  font-size: 20px;
  line-height: 30px;

  a {
    color: ${props => props.theme.confusion}!important;
  }

  @media (max-width: 960px) {
    padding: 0 50px;
  }
`;
const PostHeaderWrapper = (styled.div)`
  display: grid;
  gap: 20px;
  margin-top: 125px;
  border-top: 3px solid ${props => props.theme.excitement};
  padding-top: 39px;
  margin-bottom: 70px;

  @media (max-width: 960px) {
    margin-top: 80px;
    padding-top: 20px;
    margin-bottom: 40px;
  }
`;
const PostInner = (styled.div)`
  display: grid;
  grid-template-columns: 218px 1fr;
  grid-gap: 35px;
  align-items: flex-start;
  margin-bottom: 86px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    margin-bottom: 50px;
  }
`;
const PostSidebar = (styled.aside)`
  display: grid;
  grid-gap: 83px;

  h3 {
    font-size: inherit;
    font-weight: bold;
  }

  ul {
    margin-bottom: 0;
    list-style: none;
    padding-left: 0;

    li {
      line-height: normal;
      margin-bottom: 10px;
    }
  }

  a:hover {
    border-bottom: 1px solid currentColor;
    padding-bottom: 1px;
  }

  @media (max-width: 1280px) {
    grid-gap: 50px;
  }

  @media (max-width: 960px) {
    & > div:nth-child(2) {
      display: none;
    }
  }
`;
export const PostMain = (styled.main)`
  min-width: 0;
  font-family: "GT Sectra", serif;
  font-weight: 300;
  font-variant-numeric: lining-nums;

  h1 {
    font-weight: 400;
    font-size: 34px;
    line-height: 1.14;
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: Aeonik, sans-serif;
    font-weight: bold;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    &:not(:first-child) {
      margin-top: 1.2em;
    }
  }

  a {
    border-bottom: 1px solid currentColor;
    padding-bottom: 1px;
    &:hover {
      color: #5f748c;
    }
  }
`;
const PageLink = (styled(Link))`
  border-bottom: none !important;
`;
const CenterImage = (styled.div)`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
`;
const SplitImages = (styled.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 32px;
`;
const shortcodes = {
  Link: PageLink,
  Button,
  Image,
  pre: CodeHighlight,
  CenterImage,
  SplitImages
};
const PostHeader = ({seed, children}) => {
  const themeColors = useColors();
  const rand = create(seed);
  const accent = themeColors[rand(themeColors.length)];
  return React.createElement(PostHeaderWrapper, {
    style: {
      borderColor: accent
    }
  }, children);
};
function PageTemplate({data: {mdx, site}, children}) {
  const {siteUrl} = site.siteMetadata;
  const {author: authors, title, subtitle, date, emphasis, preview, socialPreview, sidebarButton} = mdx.frontmatter;
  const {items} = mdx.tableOfContents;
  const ogImage = socialPreview || preview;
  return React.createElement(Layout, null, React.createElement(Helmet, {
    title: title
  }, React.createElement("meta", {
    property: "og:title",
    content: title
  }), React.createElement("meta", {
    property: "og:description",
    content: mdx.excerpt
  }), ogImage && React.createElement("meta", {
    property: "og:image",
    content: siteUrl + ogImage
  }), socialPreview && React.createElement("meta", {
    name: "twitter:card",
    content: "summary_large_image"
  })), React.createElement(Wrapper, null, React.createElement(PostHeader, {
    seed: mdx.id
  }, React.createElement(PostTitle, null, renderTitleWithEmphasis(title, emphasis)), subtitle && React.createElement("h2", null, subtitle), React.createElement("div", null, date)), React.createElement(PostInner, null, React.createElement(PostSidebar, null, authors && React.createElement("div", null, React.createElement("h3", null, "Authors"), React.createElement("ul", null, authors.map((author, index) => React.createElement("li", {
    key: index,
    dangerouslySetInnerHTML: {
      __html: author
    }
  })))), items && React.createElement("div", null, React.createElement("h3", null, "Outline"), React.createElement("ul", null, items.map((item, index) => React.createElement("li", {
    key: index
  }, React.createElement("a", {
    href: item.url
  }, striptags(item.title)))))), sidebarButton && React.createElement(TesterButton, null, "Be a tester")), React.createElement(PostMain, null, React.createElement(MDXProvider, {
    components: shortcodes
  }, children)))), React.createElement(Footer));
}
export default function GatsbyMDXWrapper(props) {
  return React.createElement(PageTemplate, props, React.createElement(GATSBY_COMPILED_MDX, props));
}
export const pageQuery = graphql`
  query BlogPostQuery($id: String) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    mdx(id: { eq: $id }) {
      id
      body
      tableOfContents
      excerpt
      frontmatter {
        layout
        date(formatString: "LL")
        category
        tags
        snippet
        title
        subtitle
        emphasis
        group
        author
        preview
        socialPreview
        sidebarButton
      }
    }
  }
`;
